import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "~/atoms/checkbox";
import { Spinner } from "~/atoms/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { type Option } from "~/molecules/ControlledCombobox";
import { ComboboxCell } from "~/molecules/onboard-documents/ComboboxCell";
import { FieldStatus } from "~/molecules/FieldStatus";
import { useBulkHoverHighlight } from "~/hooks/useBulkHoverHighlight";
import { printFullName } from "~/helpers/user";
import { getTierTitle } from "~/helpers/tier";
import { type DocumentVersionSummary } from "~/api/types";
import { DocumentVersionStatus } from "~/config/types";

interface Props {
  readOnly: boolean;
  rows: DocumentVersionSummary[];
  selectedIds: Set<number>;
  allSelected: boolean;
  publishingIds: Set<number>;
  statusById: Map<number, DocumentVersionStatus>;
  onToggleAll: () => void;
  onToggleRow: (id: number) => void;
  onStatusChange: (
    id: number,
    status: DocumentVersionStatus,
    opts?: { bulk?: boolean },
  ) => void;
}

export const CompleteTable = ({
  readOnly,
  rows,
  selectedIds,
  allSelected,
  publishingIds,
  statusById,
  onToggleAll,
  onToggleRow,
  onStatusChange,
}: Props) => {
  const { t } = useTranslation();
  const { onHover, onHoverLeave, isHighlighted } =
    useBulkHoverHighlight<number>(selectedIds);

  const statusOptions = useMemo<Option[]>(
    () => [
      {
        value: DocumentVersionStatus.published,
        label: t(($) => $.commons.documentStatus.published),
      },
      {
        value: DocumentVersionStatus.draft,
        label: t(($) => $.commons.documentStatus.draft),
      },
    ],
    [t],
  );

  return (
    <Table wrapperClassName="rounded-md border">
      <TableHeader>
        <TableRow>
          <TableHead>
            <Checkbox
              checked={allSelected}
              onCheckedChange={onToggleAll}
              aria-label={t(($) => $.commons.buttons.selectAll)}
            />
          </TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.columns
                  .title,
            )}
          </TableHead>
          <TableHead>{t(($) => $.commons.document.tier)}</TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.columns
                  .category,
            )}
          </TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.columns
                  .owner,
            )}
          </TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.columns
                  .publisher,
            )}
          </TableHead>
          <TableHead>
            {t(
              ($) =>
                $.library.onboardDocuments.steps.configureAndPublish.columns
                  .status,
            )}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <CompleteRow
            readOnly={readOnly}
            key={row.id}
            row={row}
            isSelected={selectedIds.has(row.id)}
            isPublishing={publishingIds.has(row.id)}
            status={statusById.get(row.id) ?? DocumentVersionStatus.published}
            statusOptions={statusOptions}
            highlighted={isHighlighted(row.id)}
            onHover={() => onHover(row.id)}
            onHoverLeave={onHoverLeave}
            onToggle={() => onToggleRow(row.id)}
            onStatusChange={(status) =>
              onStatusChange(row.id, status, { bulk: true })
            }
          />
        ))}
      </TableBody>
    </Table>
  );
};

interface CompleteRowProps {
  readOnly: boolean;
  row: DocumentVersionSummary;
  isSelected: boolean;
  isPublishing: boolean;
  status: DocumentVersionStatus;
  statusOptions: Option[];
  highlighted: boolean;
  onHover: () => void;
  onHoverLeave: () => void;
  onToggle: () => void;
  onStatusChange: (status: DocumentVersionStatus) => void;
}

const CompleteRow = ({
  readOnly,
  row,
  isSelected,
  isPublishing,
  status,
  statusOptions,
  highlighted,
  onHover,
  onHoverLeave,
  onToggle,
  onStatusChange,
}: CompleteRowProps) => {
  const { t } = useTranslation();
  return (
    <TableRow>
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          aria-label={t(($) => $.commons.buttons.selectItem, {
            name: row.title,
          })}
        />
      </TableCell>
      <TableCell>{row.title}</TableCell>
      <TableCell>{getTierTitle(row.document.tier?.title, t)}</TableCell>
      <TableCell>{row.document.category.title}</TableCell>
      <TableCell>{printFullName(row.document.owner)}</TableCell>
      <TableCell>{printFullName(row.document.publisher)}</TableCell>
      <TableCell>
        {isPublishing ? (
          <div className="flex h-8 items-center justify-center">
            <Spinner className="size-4" />
          </div>
        ) : readOnly ? (
          <FieldStatus status={row.status} />
        ) : (
          <ComboboxCell
            value={status}
            onChange={(value) => onStatusChange(value as DocumentVersionStatus)}
            options={statusOptions}
            highlighted={highlighted}
            onHover={onHover}
            onHoverLeave={onHoverLeave}
          />
        )}
      </TableCell>
    </TableRow>
  );
};
